import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { Button } from "@zapier/design-system";
import {
  emitEventOnInterfacesPlanUpgradeSetter,
  emitEventOnTablesPlanUpgradeSetter,
  emitEventOnChatbotsPlanUpgradeSetter,
  upgradeBannerPropsAtom_SUSPENDS,
} from "@zapier/canvas-state";
import { useTabFocus } from "../../hooks/useTabFocus";
import { getNewProductUpgradeHref } from "../../utils/getNewProductUpgradeHref";
import styles from "./UpgradeButton.module.css";

interface UpgradeButtonProps {
  isBold?: boolean;
}

export function UpgradeButton({ isBold }: UpgradeButtonProps) {
  const { tablesPlanType, interfacesPlanType, chatbotsPlanType } =
    useAtomValue(upgradeBannerPropsAtom_SUSPENDS) || {};

  useTabFocus();

  const emitEventOnTablesPlanUpgrade = useSetAtom(
    emitEventOnTablesPlanUpgradeSetter,
  );
  const emitEventOnInterfacesPlanUpgrade = useSetAtom(
    emitEventOnInterfacesPlanUpgradeSetter,
  );
  const emitEventOnChatbotsPlanUpgrade = useSetAtom(
    emitEventOnChatbotsPlanUpgradeSetter,
  );

  const handleEmitEvents = useCallback(() => {
    const TABLES_EVENT_NAME = `Upgrade Tables - ${location}`;
    const INTERFACES_EVENT_NAME = `Upgrade Interfaces - ${location}`;
    const CHATBOTS_EVENT_NAME = `Upgrade Chatbots - ${location}`;

    if (tablesPlanType) {
      emitEventOnTablesPlanUpgrade(TABLES_EVENT_NAME);
    }

    if (interfacesPlanType) {
      emitEventOnInterfacesPlanUpgrade(INTERFACES_EVENT_NAME);
    }

    if (chatbotsPlanType) {
      emitEventOnChatbotsPlanUpgrade(CHATBOTS_EVENT_NAME);
    }
  }, [
    emitEventOnInterfacesPlanUpgrade,
    emitEventOnTablesPlanUpgrade,
    emitEventOnChatbotsPlanUpgrade,
    interfacesPlanType,
    tablesPlanType,
    chatbotsPlanType,
  ]);

  const handleUpgrade = useCallback(() => {
    handleEmitEvents();
    const href = getNewProductUpgradeHref({
      tablesPlanType,
      interfacesPlanType,
      chatbotsPlanType,
    });
    window.open(href, "_blank");
  }, [interfacesPlanType, handleEmitEvents, tablesPlanType, chatbotsPlanType]);

  return (
    <Button
      className={isBold ? styles.bold : ""}
      variant={isBold ? "brand" : undefined}
      onPress={handleUpgrade}
    >
      Upgrade
    </Button>
  );
}
